/* Shared core for the two demo buy boxes (offer-supply, offer-twobag).
 *
 * Both boxes describe what they add to the cart with the same little recipe
 * grammar, so the merchant edits one kind of textarea in the theme editor no
 * matter which box they are looking at:
 *
 *   <flavor-key|*>=<variant id>|<qty>|<plan>
 *
 * One line per cart line item, so a tier that is really two products (the
 * 90-day supply is 3 Vanilla + 3 Chocolate) is just two lines. "*" means the
 * tier ignores the flavor tiles. <plan> is blank for a one-time purchase, a
 * bare Loop selling_plan id, or a per-frequency list "30:123,60:456" when the
 * box exposes a frequency picker.
 *
 * Multi-line-item tiers are why these boxes post to /cart/add.js instead of
 * writing into the theme's product form the way offer-selector does -- a
 * product form can only carry one variant.
 */
(function () {
  'use strict';

  var NS = {};

  /* "vanilla=123|1|30:9,60:8" -> { vanilla: [ {id, qty, plans} ] }.
   * Unparseable lines are dropped with a warning rather than throwing, so one
   * typo in the theme editor degrades that tier instead of killing the box. */
  NS.parseRecipe = function (text) {
    var out = {};
    if (!text) return out;

    text.split(/[\r\n]+/).forEach(function (raw) {
      var line = raw.trim();
      if (!line || line.charAt(0) === '#') return;

      var eq = line.indexOf('=');
      if (eq === -1) { console.warn('[offer-box] recipe line has no "=": ' + line); return; }

      var key = line.slice(0, eq).trim().toLowerCase();
      var parts = line.slice(eq + 1).split('|');
      var id = (parts[0] || '').trim();
      if (!id) { console.warn('[offer-box] recipe line has no variant id: ' + line); return; }

      var qty = parseInt((parts[1] || '1').trim(), 10);
      if (!(qty > 0)) qty = 1;

      var plans = null;
      var planRaw = (parts[2] || '').trim();
      if (planRaw) {
        plans = {};
        if (planRaw.indexOf(':') === -1) {
          plans['*'] = planRaw;
        } else {
          planRaw.split(',').forEach(function (pair) {
            var bits = pair.split(':');
            var freq = (bits[0] || '').trim();
            var plan = (bits[1] || '').trim();
            if (freq && plan) plans[freq] = plan;
          });
        }
      }

      (out[key] = out[key] || []).push({ id: id, qty: qty, plans: plans });
    });

    return out;
  };

  /* Rows for the chosen flavor, falling back to the flavor-independent "*"
   * bucket. A tier that lists both gets the flavor-specific rows only. */
  NS.resolveItems = function (recipe, flavorKey, frequency) {
    var rows = (flavorKey && recipe[flavorKey]) || recipe['*'] || [];

    return rows.map(function (row) {
      var item = { id: row.id, quantity: row.qty };
      if (row.plans) {
        var plan = row.plans[frequency] || row.plans['*'];
        if (plan) {
          item.selling_plan = plan;
        } else {
          console.warn('[offer-box] no plan for frequency ' + frequency + ' on variant ' + row.id);
        }
      }
      return item;
    });
  };

  /* Per-flavor copy lines. A [data-flavor-line] element carries the block's
   * "key=text" lines (one per flavor tile, "*" as fallback) in
   * data-flavor-lines; this swaps its text for the chosen flavor and hides it
   * when that flavor has no line. Both boxes call it from paint(). */
  NS.paintFlavorLines = function (root, flavorKey) {
    root.querySelectorAll('[data-flavor-line]').forEach(function (el) {
      var map = {};
      (el.getAttribute('data-flavor-lines') || '').split(/[\r\n]+/).forEach(function (raw) {
        var eq = raw.indexOf('=');
        if (eq > 0) map[raw.slice(0, eq).trim().toLowerCase()] = raw.slice(eq + 1).trim();
      });
      var text = map[flavorKey] || map['*'] || '';
      el.textContent = text;
      el.hidden = !text;
    });
  };

  /* Show ONLY the chosen cover in the product gallery.
   *
   * The section renders one cover slide per offer / flavour / variant
   * (data-media-id="offer-cover-N", "flavor-cover-N", "variant-cover-<id>")
   * in front of the product media. Adam (2026-09-25): a customer must never
   * see another offer's cover, so the other covers are filtered out of the
   * slideshow and their thumbnails parked in .hidden-thumbnails (the theme's
   * own container for that), and a choice without a cover shows the plain
   * product photos. slick owns the gallery, so everything goes through it.
   * cover = '' means "no cover for this choice". */
  NS.showCover = function (root, cover, attempt) {
    if (!window.jQuery) return;
    var $ = window.jQuery;
    var scope = root.closest('.shopify-section') || document;
    var $gallery = $(scope).find('.gallery').first();
    var $slideshow = $gallery.find('.product-slideshow').first();
    if (!$slideshow.length) return;
    if (!$slideshow.hasClass('slick-initialized')) {
      // First paint: slick may not have run yet. Retry briefly, then give up.
      attempt = attempt || 0;
      if (attempt < 24) setTimeout(function () { NS.showCover(root, cover, attempt + 1); }, 250);
      return;
    }
    cover = cover || '';
    if (!scope.__coverIds) {
      scope.__coverIds = $slideshow.find('.slide--offer-cover').map(function () {
        return $(this).attr('data-media-id');
      }).get();
    }
    if (cover && scope.__coverIds.indexOf(cover) === -1) cover = '';
    scope.__coverActive = cover;

    if (scope.__coverIds.length) {
      var keep = '.slide:not(.slide--offer-cover)' + (cover ? ', .slide[data-media-id="' + cover + '"]' : '');
      $slideshow.slick('slickUnfilter');
      $slideshow.slick('slickFilter', keep);
      // Same hack the theme applies after its own slickFilter.
      $slideshow.find('.slide').css({ position: '', left: '', top: '', zIndex: '', opacity: '' });
    }

    var goTo = function () {
      var $slide = cover
        ? $slideshow.find('.slide[data-media-id="' + cover + '"]').first()
        : $slideshow.find('.slide:not(.slide--offer-cover)').first();
      if (!$slide.length) return;
      // Position within slick's live slide list, NOT data-slick-index: this
      // slick build keeps the original numbering after slickFilter, so the
      // attribute is off by however many covers were filtered out.
      var slick = $slideshow.slick('getSlick');
      var index = slick && slick.$slides ? slick.$slides.index($slide) : -1;
      if (index < 0) index = parseInt($slide.attr('data-slick-index'), 10);
      if (!isNaN(index) && index >= 0) $slideshow.slick('slickGoTo', index, true);
    };
    goTo();
    setTimeout(goTo, 120);

    NS.filterCoverThumbs($gallery, cover);

    // The theme re-shows every thumbnail whenever it (re)builds the thumbnail
    // carousel (breakpoint change); re-apply the active cover afterwards.
    if (!scope.__coverThumbHook) {
      scope.__coverThumbHook = true;
      $gallery.find('.thumbnails').first().on('initialized.owl.carousel', function () {
        setTimeout(function () { NS.filterCoverThumbs($gallery, scope.__coverActive || ''); }, 0);
      });
    }
  };

  NS.filterCoverThumbs = function ($gallery, cover) {
    var $ = window.jQuery;
    var $thumbs = $gallery.find('.thumbnails').first();
    if (!$thumbs.length) return;
    var $hidden = $gallery.find('.hidden-thumbnails');
    if (!$hidden.length) $hidden = $('<div class="hidden-thumbnails hidden">').insertAfter($thumbs);
    var isOwl = $thumbs.hasClass('owl-carousel');

    // Bring back the cover that is now active (covers sit first in the strip).
    $hidden.find('.thumbnail--offer-cover').each(function () {
      if ($(this).attr('data-media-id') === cover) {
        $(this).show();
        if (isOwl) $thumbs.trigger('add.owl.carousel', [this, 0]);
        else $(this).prependTo($thumbs);
      }
    });
    // Park every other cover thumbnail.
    $thumbs.find('.thumbnail--offer-cover').each(function () {
      if ($(this).attr('data-media-id') !== cover) {
        var idx = $(this).parent().index();
        $(this).appendTo($hidden);
        if (isOwl) $thumbs.trigger('remove.owl.carousel', [idx]);
      }
    });
    if (isOwl) $thumbs.trigger('refresh.owl.carousel');
  };

  /* Mirrors the bundle-builder's add: Rebuy's drawer owns the cart on these
   * pages when it is loaded, otherwise fall back to the theme drawer. */
  NS.addToCart = function (items) {
    var url = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';

    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        return res.json().then(function (body) { return { ok: res.ok, body: body }; });
      })
      .then(function (r) {
        if (!r.ok) throw new Error((r.body && r.body.description) || 'Unable to add to cart');

        if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
          try { window.Rebuy.SmartCart.show(); } catch (e) {}
        } else {
          document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false }));
        }
        return r.body;
      });
  };

  /* The CTA carries "<s>$130.00</s> $104.00 | ADD TO CART". Both halves are
   * plain settings strings -- these boxes never compute a price, so what the
   * client signed off on is what renders. */
  NS.renderCta = function (btn, compare, price, label) {
    if (!btn) return;
    var html = '';
    if (compare) html += '<s>' + compare + '</s> ';
    if (price) html += '<span class="offer-box__cta-price">' + price + '</span> <span class="offer-box__cta-sep">|</span> ';
    html += '<span class="offer-box__cta-label">' + (label || 'ADD TO CART') + '</span>';
    btn.innerHTML = html;
  };

  window.NutsolaOfferBox = NS;
})();
